import React from 'react';
import { useNavigate } from 'react-router-dom';
import './DashboardStatCard.css';

export type DashboardStatTone = 'primary' | 'success' | 'warning' | 'info' | 'danger' | 'neutral';

export type DashboardStatCardProps = {
  title: string;
  value: React.ReactNode;
  description?: React.ReactNode;
  /** Bootstrap icon classes, e.g. `bi bi-calendar-event` */
  icon?: string;
  tone?: DashboardStatTone;
  /** When set, the card navigates here on activate */
  to?: string;
  onClick?: () => void;
  className?: string;
  /**
   * `tile` — overview-style white feature card
   * `panel` — denser metric panel (projects-style)
   * `panel-emphasis` — filled primary metric panel
   */
  variant?: 'tile' | 'panel' | 'panel-emphasis';
};

const DashboardStatCard: React.FC<DashboardStatCardProps> = ({
  title,
  value,
  description,
  icon,
  tone = 'primary',
  to,
  onClick,
  className = '',
  variant = 'tile',
}) => {
  const navigate = useNavigate();
  const interactive = Boolean(to || onClick);

  const handleActivate = () => {
    onClick?.();
    if (to) navigate(to);
  };

  const rootClass = [
    'dashboard-stat-card',
    `dashboard-stat-card--${variant}`,
    `dashboard-stat-card--${tone}`,
    interactive ? 'dashboard-stat-card--interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const body = (
    <>
      {variant === 'tile' ? (
        <>
          <div className="dashboard-stat-card__top">
            <div className="dashboard-stat-card__title">{title}</div>
            {icon ? (
              <span className="dashboard-stat-card__icon" aria-hidden>
                <i className={icon} />
              </span>
            ) : null}
          </div>
          <div className="dashboard-stat-card__value">{value}</div>
          {description != null && description !== '' ? (
            <div className="dashboard-stat-card__description">{description}</div>
          ) : null}
        </>
      ) : (
        <>
          {icon ? (
            <span className="dashboard-stat-card__icon" aria-hidden>
              <i className={icon} />
            </span>
          ) : null}
          <div className="dashboard-stat-card__title">{title}</div>
          <div className="dashboard-stat-card__value">{value}</div>
          {description != null && description !== '' ? (
            <div className="dashboard-stat-card__description">{description}</div>
          ) : null}
        </>
      )}
    </>
  );

  if (!interactive) {
    return <div className={rootClass}>{body}</div>;
  }

  return (
    <div
      className={rootClass}
      role="link"
      tabIndex={0}
      aria-label={`Open ${title}`}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleActivate();
        }
      }}
    >
      {body}
    </div>
  );
};

export default DashboardStatCard;
